import { twMerge as cn } from 'tailwind-merge';
import { useAuth } from '../../contexts/AuthContext';
import { isPlatformAdminUser } from '../../types/onboarding';

interface ActiveOrganizationNoticeProps {
  className?: string;
}

/** Shown for org members (maintainers and developers) scoped to the active workspace. */
export function ActiveOrganizationNotice({
  className,
}: ActiveOrganizationNoticeProps) {
  const { currentUser, memberships } = useAuth();
  const isPlatformAdmin = isPlatformAdminUser(currentUser);
  const activeOrgName = currentUser?.organization;

  if (isPlatformAdmin || !activeOrgName) {
    return null;
  }

  const hasMultipleOrgs = memberships.length > 1;
  const isDeveloper =
    currentUser?.platformRole === 'developer' && !isPlatformAdmin;

  return (
    <p className={cn('text-sm text-gray-600', className)}>
      You are working in <strong>{activeOrgName}</strong>.
      {hasMultipleOrgs
        ? ' Use the organization menu in the page header to switch workspace.'
        : isDeveloper
        ? ' You can view projects assigned to you in this organization.'
        : ' Invites, project changes, and user edits apply to this organization only.'}
    </p>
  );
}

interface PlatformAdminOrgNoticeProps {
  organizationName: string;
  entityLabel: 'project' | 'user';
  className?: string;
}

/** Shown when an Admin picks which client organization an action targets. */
export function PlatformAdminOrgNotice({
  organizationName,
  entityLabel,
  className,
}: PlatformAdminOrgNoticeProps) {
  if (!organizationName.trim()) {
    return null;
  }

  return (
    <p
      className={cn(
        'text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2',
        className
      )}
    >
      This {entityLabel} will be created under organization{' '}
      <strong>{organizationName}</strong>.
    </p>
  );
}

export default ActiveOrganizationNotice;
